import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, User, ArrowLeft, LogIn, UserPlus, KeyRound, AlertTriangle } from 'lucide-react';
import './AuthPage.css';

// URL du logo Place des Énergies
const LOGO_URL = "/logo-pde.png";

const AuthPage = ({ onClose, isRequired = false }) => {
  const [mode, setMode] = useState('login'); // 'login', 'register', 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  const { login, register, forgotPassword } = useAuth();

  // Validation email @placedesenergies.com pour l'inscription
  const isValidEmail = (email) => {
    return email.toLowerCase().endsWith('@placedesenergies.com');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (mode === 'login') {
        await login(email, password);
        onClose?.();
      } else if (mode === 'register') {
        // Vérifier que l'email est @placedesenergies.com
        if (!isValidEmail(email)) {
          throw new Error('Seules les adresses email @placedesenergies.com sont autorisées');
        }
        await register(email, password, name);
        onClose?.();
      } else if (mode === 'forgot') {
        const result = await forgotPassword(email);
        setSuccess(result.message);
        if (result.new_password) {
          setNewPassword(result.new_password);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderForm = () => {
    switch (mode) {
      case 'register':
        return (
          <>
            <h2 className="auth-title">Créer un compte</h2>
            <p className="auth-subtitle">Rejoignez Place des Énergies</p>
            
            <div className="auth-restriction-notice">
              <AlertTriangle size={16} />
              <span>Inscription réservée aux collaborateurs Place des Énergies</span>
            </div>
            
            <form onSubmit={handleSubmit} className="auth-form">
              <div className="input-group">
                <User size={20} className="input-icon" />
                <input
                  type="text"
                  placeholder="Nom complet"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  data-testid="register-name-input"
                />
              </div>
              
              <div className="input-group">
                <Mail size={20} className="input-icon" />
                <input
                  type="email"
                  placeholder="prenom.nom@placedesenergies.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="register-email-input"
                  className={email && !isValidEmail(email) ? 'input-error' : ''}
                />
              </div>
              {email && !isValidEmail(email) && (
                <div className="email-hint-error">
                  <AlertTriangle size={14} />
                  L'email doit se terminer par @placedesenergies.com
                </div>
              )}
              
              <div className="input-group">
                <Lock size={20} className="input-icon" />
                <input
                  type="password"
                  placeholder="Mot de passe"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
              </div>

              {error && <div className="auth-error">{error}</div>}
              
              <button 
                type="submit" 
                className="auth-button" 
                disabled={loading || (email && !isValidEmail(email))} 
                data-testid="register-submit-btn"
              >
                {loading ? 'Création...' : (
                  <>
                    <UserPlus size={18} />
                    Créer mon compte
                  </>
                )}
              </button>
            </form>
            
            <p className="auth-switch">
              Déjà un compte ?{' '}
              <button onClick={() => { setMode('login'); setError(''); }} className="link-button">
                Se connecter
              </button>
            </p>
          </>
        );

      case 'forgot':
        return (
          <>
            <button onClick={() => { setMode('login'); setError(''); setSuccess(''); setNewPassword(''); }} className="back-button">
              <ArrowLeft size={18} />
              Retour
            </button>
            
            <h2 className="auth-title">Mot de passe oublié</h2>
            <p className="auth-subtitle">Entrez votre email pour recevoir un nouveau mot de passe</p>
            
            <form onSubmit={handleSubmit} className="auth-form">
              <div className="input-group">
                <Mail size={20} className="input-icon" />
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="forgot-email-input"
                />
              </div>

              {error && <div className="auth-error">{error}</div>}
              {success && (
                <div className="auth-success">
                  {success}
                  {newPassword && (
                    <div className="new-password-box">
                      <strong>Nouveau mot de passe :</strong>
                      <code>{newPassword}</code>
                      <p className="password-note">Notez ce mot de passe, il ne sera plus affiché.</p>
                    </div>
                  )}
                </div>
              )}
              
              {!newPassword && (
                <button type="submit" className="auth-button" disabled={loading} data-testid="forgot-submit-btn">
                  {loading ? 'Envoi...' : (
                    <>
                      <KeyRound size={18} />
                      Réinitialiser
                    </>
                  )}
                </button>
              )}
            </form>
          </>
        );

      default: // login
        return (
          <>
            <h2 className="auth-title">Connexion</h2>
            <p className="auth-subtitle">Accédez à votre espace Place des Énergies</p>
            
            <form onSubmit={handleSubmit} className="auth-form">
              <div className="input-group">
                <Mail size={20} className="input-icon" />
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                />
              </div>
              
              <div className="input-group">
                <Lock size={20} className="input-icon" />
                <input
                  type="password"
                  placeholder="Mot de passe"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                />
              </div>

              <button 
                type="button" 
                onClick={() => { setMode('forgot'); setError(''); }} 
                className="forgot-link"
              >
                Mot de passe oublié ?
              </button>

              {error && <div className="auth-error">{error}</div>}
              
              <button type="submit" className="auth-button" disabled={loading} data-testid="login-submit-btn">
                {loading ? 'Connexion...' : (
                  <>
                    <LogIn size={18} />
                    Se connecter
                  </>
                )}
              </button>
            </form>
            
            <p className="auth-switch">
              Pas encore de compte ?{' '}
              <button onClick={() => { setMode('register'); setError(''); }} className="link-button">
                Créer un compte
              </button>
            </p>
          </>
        );
    }
  };

  return (
    <div className={`auth-overlay ${isRequired ? 'auth-required-mode' : ''}`} onClick={isRequired ? undefined : onClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        {!isRequired && (
          <button className="close-button" onClick={onClose} data-testid="auth-close-btn">×</button>
        )}
        <div className="auth-logo">
          <img src={LOGO_URL} alt="Place des Énergies" />
        </div>
        {isRequired && (
          <div className="auth-required-message">
            <p>Connexion requise pour accéder à l'application</p>
          </div>
        )}
        {renderForm()}
      </div>
    </div>
  );
};

export default AuthPage;
